// ECONIC number formatting (DESIGN-SYSTEM §4, §13.2; SPEC §23). Owned by WP-F0-WEB, FROZEN after gate G0.
//
// One rule set for every native screen, so the same fact reads the same everywhere:
//   - Western digits in both languages, thousands ",", decimal "." (UAE government practice);
//   - a true minus U+2212 and an explicit "+" for signed deltas; percent, "pp" and "×" follow the figure;
//   - in Arabic text only the figure is isolated left-to-right (`<bdi dir="ltr">`), never the Arabic words around it;
//   - a missing value is "—", never 0.
// `formatNumber` returns the isolated element; `numberText` returns the bare string for places that cannot hold an element (SVG text,
// `aria-label`, `title`), where `isolate()` in ./bidi adds Unicode isolates instead.
import { createElement, type ReactElement } from "react";

export type NumberLocale = "en" | "ar" | (string & {});

export const MINUS = "−";
export const NO_VALUE = "—";

/** Unit written after the figure. A currency code (`AED`, `USD`) is written after it too, with a space ("12.4 bn AED" style is the caller's). */
export type NumberUnit = "%" | "pp" | "×" | "x" | (string & {});

export interface NumberOptions {
  /** Exact number of decimals; overrides min/max. */
  decimals?: number;
  minDecimals?: number;
  /** Default 1 for percent and pp, 2 for × and otherwise 0–2 by size (see `defaultDecimals`). */
  maxDecimals?: number;
  /** Write "+" for positive values (deltas, changes); zero stays unsigned. */
  signed?: boolean;
  unit?: NumberUnit;
  /** 1.5M / 2.4B in English, 1.5 مليون / 2.4 مليار in Arabic (the word stays outside the isolate). */
  compact?: boolean;
  /** Grouping separators (default true). */
  grouping?: boolean;
}

/** The pieces of a formatted figure: what goes inside the left-to-right isolate, and a word that stays outside it (compact Arabic). */
export interface NumberParts {
  /** "−1,234.5", "+2.8%", "1.5". */
  figure: string;
  /** " مليون" for compact Arabic, otherwise "". */
  word: string;
  /** false for a missing value ("—"). */
  ok: boolean;
}

const BIDI_MARKS = /[؜‎‏‪-‮⁦-⁩]/g;

const intlLocale = (locale: NumberLocale): string => (String(locale).startsWith("ar") ? "ar-AE" : "en-AE");

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : typeof value === "bigint" ? Number(value) : Number(String(value).replace(/[,\s]/g, "").replace(MINUS, "-"));
  return Number.isFinite(n) ? n : null;
}

function defaultDecimals(n: number, unit: NumberUnit | undefined, compact: boolean | undefined): [number, number] {
  if (unit === "%" || unit === "pp") return [0, 1];
  if (unit === "×" || unit === "x") return [0, 2];
  if (compact) return [0, 1];
  const abs = Math.abs(n);
  if (abs >= 1000) return [0, 0];
  if (abs >= 100) return [0, 1];
  return [0, 2];
}

function unitText(unit: NumberUnit | undefined): { before: string; after: string } {
  if (!unit) return { before: "", after: "" };
  if (unit === "%") return { before: "", after: "%" };
  if (unit === "pp") return { before: "", after: " pp" };
  if (unit === "×" || unit === "x") return { before: "", after: "×" };
  return { before: "", after: ` ${unit}` };
}

/** Splits a value into its figure and (compact Arabic) word, applying every rule above. */
export function numberParts(value: unknown, locale: NumberLocale, options: NumberOptions = {}): NumberParts {
  const n = toNumber(value);
  if (n === null) return { figure: NO_VALUE, word: "", ok: false };
  const [defMin, defMax] = defaultDecimals(n, options.unit, options.compact);
  const minimumFractionDigits = options.decimals ?? options.minDecimals ?? defMin;
  const maximumFractionDigits = Math.max(minimumFractionDigits, options.decimals ?? options.maxDecimals ?? defMax);
  const format = new Intl.NumberFormat(intlLocale(locale), {
    numberingSystem: "latn",
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping: options.grouping ?? true,
    ...(options.compact ? { notation: "compact" as const, compactDisplay: "short" as const } : {}),
  });
  // The absolute value is formatted and the sign written here: Intl's Arabic output carries bidi marks and a hyphen-minus.
  let digits = "";
  let word = "";
  for (const part of format.formatToParts(Math.abs(n))) {
    if (part.type === "compact") word += part.value;
    else if (part.type === "literal" && word) word += part.value;
    else if (part.type === "literal") digits += part.value;
    else digits += part.value;
  }
  digits = digits.replace(BIDI_MARKS, "").trim();
  // A value that rounds to zero is written without a sign ("0.0", never "−0.0").
  const zero = Number(digits.replace(/,/g, "")) === 0;
  const sign = zero ? "" : n < 0 ? MINUS : options.signed ? "+" : "";
  const { before, after } = unitText(options.unit);
  const isArabicWord = /[؀-ۿ]/.test(word);
  if (isArabicWord) {
    // "1.5 مليون": the figure is isolated, the word follows it in the Arabic text; a unit stays with the figure.
    return { figure: `${sign}${before}${digits}${after}`, word: ` ${word.replace(BIDI_MARKS, "").trim()}`, ok: true };
  }
  return { figure: `${sign}${before}${digits}${word.replace(BIDI_MARKS, "").trim()}${after}`, word: "", ok: true };
}

/** The formatted figure as plain text ("−1,234.5", "+2.8%", "1.5 مليون"). For element contexts use `formatNumber`. */
export function numberText(value: unknown, locale: NumberLocale, options: NumberOptions = {}): string {
  const p = numberParts(value, locale, options);
  return `${p.figure}${p.word}`;
}

/**
 * The formatted figure as an element: `<bdi dir="ltr">−1,234.5</bdi>`, so signs and units keep their place inside Arabic text.
 * A compact Arabic word follows the isolate. A missing value renders "—" (not isolated, not zero).
 */
export function formatNumber(value: unknown, locale: NumberLocale, options: NumberOptions = {}): ReactElement {
  const p = numberParts(value, locale, options);
  if (!p.ok) return createElement("span", { "data-no-value": "" }, NO_VALUE);
  const figure = createElement("bdi", { dir: "ltr", className: "tnum" }, p.figure);
  return p.word ? createElement("span", null, figure, p.word) : figure;
}

/** A share given as a percentage number (5.54 → "5.54%"); `fraction: true` reads 0.0554 as 5.54 %. */
export function percentText(value: unknown, locale: NumberLocale, options: Omit<NumberOptions, "unit"> & { fraction?: boolean } = {}): string {
  const n = toNumber(value);
  const { fraction, ...rest } = options;
  return numberText(n === null ? null : fraction ? n * 100 : n, locale, { ...rest, unit: "%" });
}

/** A signed change: "+0.4 pp", "−1.2%", "+3". */
export function deltaText(value: unknown, locale: NumberLocale, unit?: NumberUnit, options: Omit<NumberOptions, "unit" | "signed"> = {}): string {
  return numberText(value, locale, { ...options, unit, signed: true });
}

/** A count on a badge: "99+" above 99 (or `cap`), "n+" when the count is only a lower bound. Never negative. */
export function countText(value: number | null | undefined, locale: NumberLocale, options: { cap?: number; atLeast?: boolean } = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NO_VALUE;
  const cap = options.cap ?? 99;
  const n = Math.max(0, Math.trunc(value));
  if (n > cap) return `${numberText(cap, locale, { decimals: 0 })}+`;
  return options.atLeast ? `${numberText(n, locale, { decimals: 0 })}+` : numberText(n, locale, { decimals: 0 });
}

/** Whether a value counts as a figure (not null, not NaN). */
export const hasValue = (value: unknown): boolean => toNumber(value) !== null;
